import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, Modal, Spin, Tag, Typography } from 'antd';
import { Link } from 'react-router-dom';

import { errorMessage } from '../../MarketExplorer/api';
import {
  fetchVersion,
  isJobFailed,
  isSaveSettled,
  jobMessage,
  saveProposal,
  waitForSave,
} from './api';
import { GapList, SavedExtras } from './DataCoverage';
import { curatedRowCount, curatedRowsNote, isBlocker, plural } from './format';
import type { SaveTarget } from './PreviewDrawer';
import {
  ExcludedInstrument,
  Preview,
  Proposal,
  ProposalVersion,
  SavePayload,
} from './types';

type Phase = 'edit' | 'saving' | 'done';

type Props = {
  target: SaveTarget | null;
  onClose: () => void;
  /** Told once the save has an outcome (or the POST was accepted). */
  onSettled: (proposal: Proposal) => void;
};

/** The blueprint page, in the route shape the rest of the admin links to. */
export const blueprintHref = (blueprintId: number) =>
  `/dashboard/blueprints/${blueprintId}`;

/** The gaps that stop a save; none for a version without a data check. */
export const saveBlockers = (preview: Preview | null | undefined) =>
  (preview?.data_status?.gaps || []).filter(isBlocker);

/**
 * What the save would leave out, by the backend's own rule
 * (`save.split_instruments`): each untradable instrument with its reason,
 * plus codes the preview could not place in the builder universe (their
 * `tradable` gap). An instrument with a BLOCKER is not listed: the save is
 * refused until it is fixed, so it is not "left out" -- the blocker alert
 * names it instead.
 */
export const excludedInstruments = (preview: Preview): ExcludedInstrument[] => {
  const coverage = preview.data_status;
  if (!coverage) return [];
  const gaps = coverage.gaps || [];
  const blocked = new Set(gaps.filter(isBlocker).map(gap => gap.code));
  const excluded: ExcludedInstrument[] = (coverage.instruments || [])
    .filter(row => !row.tradable?.ok && !blocked.has(row.code))
    .map(row => ({ code: row.code, reason: row.tradable?.reason || '' }));
  const listed = new Set((preview.instruments || []).map(item => item.code));
  gaps.forEach(gap => {
    if (
      gap.kind === 'tradable' &&
      gap.code &&
      !listed.has(gap.code) &&
      !excluded.some(entry => entry.code === gap.code)
    )
      excluded.push({ code: gap.code, reason: gap.detail });
  });
  return excluded;
};

/**
 * What the saved blueprint will hold: the instruments it trades, the ones it
 * leaves out (untradable instruments are excluded by name, never silently),
 * and the AI Curated rows it writes. A version built before the data check
 * cannot say which instruments are tradable, so it lists them unsplit and
 * shows its failed checks: the save refuses a failed price or family check.
 */
const SaveContents: React.FC<{ preview: Preview }> = ({ preview }) => {
  const coverage = preview.data_status;
  const failed = coverage
    ? []
    : preview.checks.filter(check => check.status === 'fail');
  const traded = (coverage?.instruments || []).filter(row => row.tradable?.ok);
  const excluded = excludedInstruments(preview);
  return (
    <div
      className="blueprint-builder__save-contents"
      data-testid="save-contents"
    >
      {coverage ? (
        <>
          <div>
            <Typography.Text strong>
              Traded in the saved blueprint
            </Typography.Text>
            <div className="blueprint-builder__save-codes">
              {traded.length ? (
                traded.map(row => <Tag key={row.code}>{row.code}</Tag>)
              ) : (
                <Typography.Text type="secondary">None</Typography.Text>
              )}
            </div>
          </div>
          <div>
            <Typography.Text strong>Not included</Typography.Text>
            {excluded.length ? (
              <ul className="blueprint-builder__save-excluded">
                {excluded.map(item => (
                  <li key={item.code}>
                    <Typography.Text strong>{item.code}</Typography.Text>
                    {item.reason ? `: ${item.reason}` : ''}
                  </li>
                ))}
              </ul>
            ) : (
              <div>
                <Typography.Text type="secondary">
                  {traded.length === (coverage.instruments || []).length
                    ? 'Nothing: every instrument is traded.'
                    : 'Nothing is left out: the blocked instruments must be fixed before saving.'}
                </Typography.Text>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <Typography.Text type="secondary">
            This version was built before the data check, so which of{' '}
            {preview.instruments.map(item => item.code).join(', ') ||
              'its instruments'}{' '}
            are traded is settled when saving.
          </Typography.Text>
          {failed.length > 0 && (
            <Alert
              type="warning"
              showIcon
              title="The save may refuse this version"
              description={
                <ul className="blueprint-builder__save-excluded">
                  {failed.map(check => (
                    <li key={check.key}>
                      {check.label}
                      {check.detail ? `: ${check.detail}` : ''}
                    </li>
                  ))}
                </ul>
              }
            />
          )}
        </>
      )}
      <Typography.Text data-testid="curated-note">
        {curatedRowsNote(curatedRowCount(preview))}
      </Typography.Text>
    </div>
  );
};

/**
 * Save as a DRAFT blueprint. The trainee title and brief are prefilled from
 * the version's `preview.trainee` and sent as edited; after `save/` the
 * proposal is re-read until `saved.status` is `saved` or `failed`.
 *
 * Confirm stays disabled while the preview has a blocking data gap: the
 * backend would refuse it anyway, and the dialog shows each blocker's remedy
 * instead of a failed job.
 */
const SaveModal: React.FC<Props> = ({ target, onClose, onSettled }) => {
  const [version, setVersion] = useState<ProposalVersion | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [title, setTitle] = useState('');
  const [brief, setBrief] = useState('');
  const [phase, setPhase] = useState<Phase>('edit');
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [error, setError] = useState('');
  const closed = useRef(false);

  useEffect(() => {
    closed.current = target == null;
    setPhase('edit');
    setProposal(null);
    setError('');
    setLoadError('');
    if (!target) return undefined;
    const prefill = (value: ProposalVersion) => {
      setVersion(value);
      setTitle(value.preview.trainee.title || '');
      setBrief(value.preview.trainee.brief || '');
    };
    if (target.version && target.version.id === target.versionId) {
      prefill(target.version);
      return undefined;
    }
    setVersion(null);
    setTitle('');
    setBrief('');
    const controller = new AbortController();
    setLoading(true);
    fetchVersion(target.versionId, controller.signal)
      .then(value => {
        if (!controller.signal.aborted) prefill(value);
      })
      .catch(reason => {
        if (!controller.signal.aborted)
          setLoadError(
            errorMessage(reason) || 'The trainee text could not be loaded.',
          );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [target]);

  const close = () => {
    closed.current = true;
    onClose();
  };

  const confirm = async () => {
    if (!target || saveBlockers(version?.preview).length) return;
    setError('');
    setPhase('saving');
    const payload: SavePayload = { version_id: target.versionId };
    if (title.trim()) payload.trainee_title = title.trim();
    if (brief.trim()) payload.trainee_brief = brief.trim();
    let accepted: Proposal;
    try {
      const response = await saveProposal(target.proposalId, payload);
      accepted = response.proposal;
      setProposal(accepted);
      if (
        response.job &&
        isJobFailed(response.job) &&
        !isSaveSettled(accepted)
      ) {
        setError(jobMessage(response.job));
        setPhase('done');
        onSettled(accepted);
        return;
      }
    } catch (reason) {
      // Refused at the door: nothing was saved, and the form stays editable.
      setError(errorMessage(reason) || 'The save could not be started.');
      setPhase('edit');
      return;
    }
    try {
      const final = await waitForSave(accepted, {
        onUpdate: setProposal,
        cancelled: () => closed.current,
      });
      if (closed.current) {
        onSettled(final);
        return;
      }
      setProposal(final);
      onSettled(final);
    } catch (reason) {
      setError(errorMessage(reason) || 'The save status could not be read.');
      onSettled(accepted);
    }
    setPhase('done');
  };

  const saved = proposal?.saved;
  const leaks = version?.preview.trainee.leaks || [];
  const blockers = saveBlockers(version?.preview);

  const footer =
    phase === 'edit'
      ? [
          <Button key="cancel" onClick={close}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            disabled={loading || !title.trim() || blockers.length > 0}
            onClick={() => void confirm()}
          >
            Save as draft
          </Button>,
        ]
      : [
          <Button key="close" onClick={close}>
            Close
          </Button>,
        ];

  return (
    <Modal
      open={target != null}
      title={target ? `Save "${target.title}" as a draft blueprint` : ''}
      onCancel={close}
      footer={footer}
      destroyOnHidden
      width={640}
    >
      {phase === 'edit' && (
        <div className="blueprint-builder__modal">
          <Typography.Paragraph type="secondary">
            Saving creates a DRAFT blueprint with its answer key
            {version ? ` from version ${version.number}` : ''}. A person still
            publishes it.
          </Typography.Paragraph>
          {error && <Alert type="error" showIcon title={error} />}
          {loadError && <Alert type="warning" showIcon title={loadError} />}
          {blockers.length > 0 && (
            <Alert
              type="error"
              showIcon
              data-testid="save-blockers"
              title={`Saving is blocked by ${plural(blockers.length, 'data gap')}`}
              description={
                <>
                  <GapList gaps={blockers} />
                  <div>
                    Add what is missing, then ask the builder to rebuild this
                    version.
                  </div>
                </>
              }
            />
          )}
          {loading ? (
            <Spin description="Loading the trainee text" />
          ) : (
            <>
              <label className="blueprint-builder__field">
                <span>Trainee title</span>
                <Input
                  aria-label="Trainee title"
                  value={title}
                  onChange={event => setTitle(event.target.value)}
                />
              </label>
              <label className="blueprint-builder__field">
                <span>Trainee brief</span>
                <Input.TextArea
                  aria-label="Trainee brief"
                  value={brief}
                  autoSize={{ minRows: 4, maxRows: 12 }}
                  onChange={event => setBrief(event.target.value)}
                />
              </label>
              <Typography.Text type="secondary">
                Trainees see this text. It must not name dates, people or
                outcomes.
              </Typography.Text>
              {leaks.length > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  title="The preview flagged possible leaks"
                  description={leaks.join(' · ')}
                />
              )}
              {version && <SaveContents preview={version.preview} />}
            </>
          )}
        </div>
      )}
      {phase === 'saving' && (
        <div className="blueprint-builder__center" role="status">
          <Spin description="Creating the draft blueprint…" />
          {saved?.message && (
            <Typography.Text type="secondary">{saved.message}</Typography.Text>
          )}
        </div>
      )}
      {phase === 'done' && (
        <div className="blueprint-builder__modal">
          {saved?.status === 'saved' ? (
            <Alert
              type="success"
              showIcon
              title="Draft blueprint created"
              description={
                <>
                  {saved.blueprint_id != null && (
                    <div>
                      <Link to={blueprintHref(saved.blueprint_id)}>
                        Open blueprint #{saved.blueprint_id}
                      </Link>
                    </div>
                  )}
                  {saved.message && <div>{saved.message}</div>}
                  <SavedExtras saved={saved} />
                </>
              }
            />
          ) : saved?.status === 'failed' ? (
            <Alert
              type="error"
              showIcon
              title="The save failed"
              description={saved.message || error || undefined}
            />
          ) : error ? (
            <Alert type="error" showIcon title={error} />
          ) : (
            <Alert
              type="info"
              showIcon
              title="Still saving"
              description="The save is still running. The proposal card will show the outcome."
            />
          )}
        </div>
      )}
    </Modal>
  );
};

export default SaveModal;
